import React, { useState, useEffect } from 'react';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';
import './NotificationSystem.css';

const NotificationSystem = () => {
  const [notifications, setNotifications] = useState([]);

  // Auto-remove notifications after 5 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setNotifications(prev => 
        prev.filter(notification => 
          Date.now() - notification.timestamp < 5000
        )
      );
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const addNotification = (type, title, message, duration = 5000) => {
    const notification = {
      id: Date.now() + Math.random(),
      type, // 'success', 'error', 'warning', 'info'
      title,
      message,
      timestamp: Date.now(),
      duration
    };

    setNotifications(prev => [...prev, notification]);

    // Auto-remove after duration
    setTimeout(() => {
      removeNotification(notification.id);
    }, duration);

    return notification.id;
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const getIcon = (type) => {
    switch (type) {
      case 'success': return <CheckCircle size={20} />;
      case 'error': return <AlertCircle size={20} />;
      case 'warning': return <AlertTriangle size={20} />;
      case 'info': return <Info size={20} />;
      default: return <Info size={20} />;
    }
  };

  const getColor = (type) => {
    switch (type) {
      case 'success': return '#27ae60';
      case 'error': return '#e74c3c';
      case 'warning': return '#f39c12';
      case 'info': return '#3498db';
      default: return '#3498db';
    }
  };

  // Expose addNotification function globally
  useEffect(() => {
    window.showNotification = addNotification;
    return () => {
      delete window.showNotification;
    };
  }, []);

  return (
    <div className="notification-container">
      {notifications.map(notification => (
        <div
          key={notification.id}
          className={`notification notification-${notification.type}`}
          style={{
            borderLeftColor: getColor(notification.type)
          }}
        >
          <div className="notification-icon" style={{ color: getColor(notification.type) }}>
            {getIcon(notification.type)}
          </div>
          <div className="notification-content">
            <div className="notification-title">{notification.title}</div>
            {notification.message && (
              <div className="notification-message">{notification.message}</div>
            )}
          </div>
          <button
            className="notification-close"
            onClick={() => removeNotification(notification.id)}
          >
            <X size={16} />
          </button>
        </div>
      ))}
    </div>
  );
};

// Helper functions for easy notification creation
export const showSuccess = (title, message) => {
  if (window.showNotification) {
    return window.showNotification('success', title, message);
  }
};

export const showError = (title, message) => {
  if (window.showNotification) {
    return window.showNotification('error', title, message);
  }
};

export const showWarning = (title, message) => {
  if (window.showNotification) {
    return window.showNotification('warning', title, message);
  }
};

export const showInfo = (title, message) => {
  if (window.showNotification) {
    return window.showNotification('info', title, message);
  }
};

export default NotificationSystem;